import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { Download, ArrowRight, RotateCcw, RotateCw, RefreshCw, Check, XCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { downloadFile } from '../utils/download';

const ANGLES = [
  { value: 90, label: '90° Clockwise', Icon: RotateCw },
  { value: 270, label: '90° Counter-clockwise', Icon: RotateCcw },
  { value: 180, label: '180°', Icon: RefreshCw },
];

export default function RotatePdf() {
  const [files, setFiles] = useState([]);
  const [angle, setAngle] = useState(90);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleRotate = async () => {
    if (!files.length) return toast.error('Please select a PDF file');
    trackEvent('conversion_started', { tool_name: 'Rotate PDF' });
    setStatus('processing'); setError(''); setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('angle', angle);

    try {
      const res = await axios.post('/api/convert/rotate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus('done');
      setResult(res.data);
      toast.success('PDF rotated successfully!');
    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Rotate failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); };

  return (
    <div>
      <Helmet>
        <title>Rotate PDF Online Free — Fix Sideways Pages | ILoveConvert</title>
        <meta name="description" content="Rotate PDF pages 90, 180, or 270 degrees online. Free, fast, no signup needed. Works on mobile too." />
        <meta name="keywords" content="rotate pdf, fix sideways pdf, rotate pdf pages online, rotate pdf free, pdf rotator" />
        <link rel="canonical" href="https://iloveconvert.in/rotate-pdf" />
        <meta property="og:title" content="Rotate PDF Online Free — Fix Sideways Pages | ILoveConvert" />
        <meta property="og:description" content="Rotate PDF pages 90, 180, or 270 degrees online. Free, fast, no signup needed." />
        <meta property="og:url" content="https://iloveconvert.in/rotate-pdf" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rotate PDF Online Free — Fix Sideways Pages" />
        <meta name="twitter:description" content="Rotate PDF pages 90, 180, or 270 degrees. Free, no signup needed." />
      </Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><RotateCw size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 8 }}>Rotate PDF</h1>
        <p>Fix sideways or upside-down pages in your PDF</p>
      </section>

      <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          Rotate every page in your PDF file online — perfect for documents scanned sideways
          or upside down. Choose a rotation angle, upload your PDF, and download the corrected file in seconds.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <h2>How to rotate a PDF file</h2>
        <ul>
          <li>Upload your PDF file</li>
          <li>Choose a rotation angle</li>
          <li>Click "Rotate PDF"</li>
          <li>Download your corrected PDF instantly</li>
        </ul>

        <h2>Why use this Rotate PDF tool?</h2>
        <ul>
          <li>Fast and secure processing</li>
          <li>No installation required</li>
          <li>Works on mobile and desktop</li>
          <li>Rotates every page without any quality loss</li>
        </ul>

        <h2>Best use cases</h2>
        <ul>
          <li>Fix scanned documents that came out sideways</li>
          <li>Correct upside-down pages before sharing or printing</li>
          <li>Standardize page orientation across a merged document</li>
        </ul>

        <h2>Frequently asked questions</h2>
        <FaqAccordion faqs={[
          { q: 'Is this tool free?', a: 'Yes, it is completely free to use.' },
          { q: 'Does rotating affect PDF quality?', a: 'No, rotation is a structural change — text sharpness, images, and formatting stay byte-for-byte identical.' },
          { q: 'Can I rotate just one page instead of the whole PDF?', a: 'This tool rotates every page by the same angle. To rotate only specific pages, split the PDF first, rotate the page you need, then merge it back.' },
          { q: 'Are my files secure?', a: 'Yes, files are processed securely and deleted automatically.' },
        ]} />
      </div>

      <div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={{ 'application/pdf': ['.pdf'] }} />
            {files.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10 }}>Rotation angle</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                  {ANGLES.map(a => (
                    <button
                      key={a.value}
                      onClick={() => setAngle(a.value)}
                      className={angle === a.value ? 'btn btn-primary' : 'btn btn-outline'}
                      style={{ flexDirection: 'column', gap: 6, padding: '14px 8px' }}
                    >
                      <a.Icon size={20} />
                      <span style={{ fontSize: '0.78rem' }}>{a.label}</span>
                    </button>
                  ))}
                </div>
                <button className="btn btn-primary btn-lg" onClick={handleRotate} style={{ width: '100%' }}>
                  <ArrowRight size={18} /> Rotate PDF
                </button>
              </div>
            )}
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Rotating your PDF...</p>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>PDF Rotated Successfully!</h3>
              <p>Your PDF is ready to download.</p>
              <button onClick={() => downloadFile(result.downloadUrl, result.downloadUrl.split('/').pop(), 'Rotate PDF')} className="download-btn">
                <Download size={18} /> Download Rotated PDF
              </button>
              <ShareButtons toolLabel="Rotate PDF" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Rotate Another</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Rotate Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}
      </div>
    </div>
  );
}
